// src/pages/Rates/DailyRateManager/DailyRateManager.js

import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getTodayRateThunk,
  fetchFromAPIThunk,
  confirmAPIRatesThunk,
  copyPreviousDayThunk,
  setDailyRateThunk,
  setSelectedBranch,
  getRateStatusThunk, // Add this thunk
} from "../../../store/thunks/rateThunks";

import RateComparisonCard from "../../../components/Rates/RateComparisonCard";
import RateInputForm from "../../../components/Rates/RateInputForm";
import CalendarView from "../../../components/Rates/CalendarView";
import BranchSelector from "../../../components/UI/BranchSelector";
import { toast } from "react-hot-toast";

const DailyRateManager = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Selectors
  const { user } = useSelector((state) => state.auth);
  const { dailyRates, loading, ui, errors } = useSelector(
    (state) => state.rate
  );

  // Local state
  const [mode, setMode] = useState("check");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [currentBranch, setCurrentBranch] = useState(
    ui.selectedBranch || user?.branchAccess?.primaryBranch || ""
  );
  const [isMobile, setIsMobile] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Get branch-specific data
  const branchData = dailyRates.byBranch?.[currentBranch] || {};
  const todayRate = branchData.today;
  const apiRates = branchData.apiRates;
  const rateHistory = branchData.history || {};

  // Detect mobile screen
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Initialize with primary branch
  useEffect(() => {
    if (user?.branchAccess?.primaryBranch && !currentBranch) {
      const primaryBranch = user.branchAccess.primaryBranch;
      setCurrentBranch(primaryBranch);
      dispatch(setSelectedBranch(primaryBranch));
    }
  }, [user, currentBranch, dispatch]);

  // Fetch today's rate when branch or date changes
  useEffect(() => {
    if (currentBranch && selectedDate) {
      dispatch(
        getTodayRateThunk({
          branchCode: currentBranch,
          date: selectedDate,
        })
      );
    }
  }, [dispatch, currentBranch, selectedDate]);

  // Fetch rate status for calendar when branch or month changes
  useEffect(() => {
    if (currentBranch) {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;

      dispatch(
        getRateStatusThunk({
          branchCode: currentBranch,
          year,
          month,
        })
      );
    }
  }, [dispatch, currentBranch, currentMonth]);

  // Check for query parameters
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get("action") === "fetch-market" && currentBranch) {
      handleFetchFromAPI();
      // Clear the query parameter
      navigate("/rates/daily", { replace: true });
    }
  }, [location, currentBranch]);

  // Handlers
  const handleBranchChange = useCallback(
    (branchCode) => {
      setCurrentBranch(branchCode);
      dispatch(setSelectedBranch(branchCode));
      setMode("check"); // Reset mode when branch changes
    },
    [dispatch]
  );

  const handleFetchFromAPI = useCallback(async () => {
    try {
      await dispatch(
        fetchFromAPIThunk({
          date: selectedDate,
          branchCode: currentBranch,
        })
      ).unwrap();
      setMode("api");
      toast.success("Market rates fetched successfully");
    } catch (error) {
      toast.error(error || "Failed to fetch market rates");
    }
  }, [dispatch, selectedDate, currentBranch]);

  const handleConfirmAPIRates = useCallback(async () => {
    try {
      await dispatch(
        confirmAPIRatesThunk({
          apiRates: apiRates, // Use branch-specific apiRates
          effectiveDate: selectedDate,
          branchCode: currentBranch,
        })
      ).unwrap();
      setMode("check");
      toast.success("API rates confirmed and applied");
    } catch (error) {
      toast.error(error || "Failed to confirm API rates");
    }
  }, [dispatch, apiRates, selectedDate, currentBranch]);

  const handleEditAPIRates = useCallback(() => {
    setMode("manual-from-api");
  }, []);

  const handleCopyPreviousDay = useCallback(async () => {
    try {
      await dispatch(
        copyPreviousDayThunk({
          targetDate: selectedDate,
          branchCode: currentBranch,
        })
      ).unwrap();
      toast.success("Previous day rate copied successfully");
      setMode("check");
    } catch (error) {
      toast.error(error || "Failed to copy previous day rate");
    }
  }, [dispatch, selectedDate, currentBranch]);

  const handleManualSet = useCallback(
    async (rateData) => {
      try {
        await dispatch(
          setDailyRateThunk({
            ...rateData,
            effectiveDate: selectedDate,
            branchCode: currentBranch,
            publish: true,
          })
        ).unwrap();
        setMode("check");
        toast.success("Rates set successfully");
      } catch (error) {
        toast.error(error || "Failed to set rates");
      }
    },
    [dispatch, selectedDate, currentBranch]
  );

  const handleDateSelect = useCallback((date) => {
    setSelectedDate(date);
    setMode("check"); // Reset mode when date changes
  }, []);

  const handleMonthChange = useCallback((newMonth) => {
    setCurrentMonth(newMonth);
  }, []);

  // Get the correct initial data for the input form
  const getInitialFormData = useCallback(() => {
    if (mode === "manual-from-api" && apiRates) {
      return apiRates.rates;
    }
    return todayRate?.rates;
  }, [mode, apiRates, todayRate]);

  // Check if rates are set for selected date
  const hasRatesForDate = todayRate && todayRate.effectiveDate === selectedDate;
  const isUsingFallbackRate =
    todayRate && todayRate.effectiveDate !== selectedDate;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="border-b border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                Daily Rate Management
              </h1>
              <p className="text-gray-600 text-xs sm:text-sm mt-1">
                Monitor and manage daily rates across branches
              </p>
            </div>

            <BranchSelector
              selectedBranch={currentBranch}
              onBranchChange={handleBranchChange}
              compact={isMobile}
            />
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          {/* Branch Info Banner */}
          {currentBranch && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Managing rates for:</strong> Branch {currentBranch}
                {selectedDate && (
                  <span className="ml-2">
                    | Date:{" "}
                    {new Date(selectedDate + "T00:00:00").toLocaleDateString()}
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Calendar View */}
          <div>
            <CalendarView
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              rateStatus={rateHistory}
              currentMonth={currentMonth}
              onMonthChange={handleMonthChange}
            />
          </div>

          {/* Current Rates Display */}
          {todayRate ? (
            <div
              className={`p-4 border rounded-lg ${
                hasRatesForDate
                  ? "bg-green-50 border-green-200"
                  : "bg-amber-50 border-amber-200"
              }`}
            >
              <h3
                className={`font-semibold mb-3 ${
                  hasRatesForDate ? "text-green-800" : "text-amber-800"
                }`}
              >
                {hasRatesForDate
                  ? `Active Rates for ${new Date(
                      selectedDate + "T00:00:00"
                    ).toLocaleDateString()}`
                  : `No rates set for ${new Date(
                      selectedDate + "T00:00:00"
                    ).toLocaleDateString()} - Using rates from ${new Date(
                      todayRate.effectiveDate + "T00:00:00"
                    ).toLocaleDateString()}`}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div
                  className={`bg-white p-3 rounded border ${
                    hasRatesForDate ? "border-green-300" : "border-amber-300"
                  }`}
                >
                  <span className="text-xs text-gray-600">Gold</span>
                  <p className="text-lg font-bold text-gray-900">
                    ₹
                    {todayRate.rates?.GOLD?.baseRate?.toLocaleString("en-IN") ||
                      "-"}
                    /gm
                  </p>
                </div>
                <div
                  className={`bg-white p-3 rounded border ${
                    hasRatesForDate ? "border-green-300" : "border-amber-300"
                  }`}
                >
                  <span className="text-xs text-gray-600">Silver</span>
                  <p className="text-lg font-bold text-gray-900">
                    ₹
                    {todayRate.rates?.SILVER?.baseRate?.toLocaleString(
                      "en-IN"
                    ) || "-"}
                    /gm
                  </p>
                </div>
                <div
                  className={`bg-white p-3 rounded border ${
                    hasRatesForDate ? "border-green-300" : "border-amber-300"
                  }`}
                >
                  <span className="text-xs text-gray-600">Platinum</span>
                  <p className="text-lg font-bold text-gray-900">
                    ₹
                    {todayRate.rates?.PLATINUM?.baseRate?.toLocaleString(
                      "en-IN"
                    ) || "-"}
                    /gm
                  </p>
                </div>
              </div>
              {todayRate.source && (
                <div className="mt-3 flex items-center justify-between">
                  <p
                    className={`text-sm ${
                      hasRatesForDate ? "text-green-600" : "text-amber-600"
                    }`}
                  >
                    Source: {todayRate.source}
                    {isUsingFallbackRate && " (Fallback)"}
                  </p>
                  {todayRate.publishedAt && (
                    <p className="text-xs text-gray-500">
                      Published:{" "}
                      {new Date(todayRate.publishedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
              {isUsingFallbackRate && (
                <div className="mt-3 p-2 bg-amber-100 rounded">
                  <p className="text-xs text-amber-700">
                    ⚠️ Using previous rate as fallback. You can set a specific
                    rate for this date using the options below.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-gray-600">
                No rates available. Please set rates for{" "}
                {new Date(selectedDate + "T00:00:00").toLocaleDateString()}
              </p>
            </div>
          )}

          {/* Action Buttons and Forms */}
          {currentBranch ? (
            <>
              {/* Action Buttons */}
              {mode === "check" && (
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setMode("manual")}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                             transition-colors flex items-center gap-2"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Set Manually
                  </button>
                  <button
                    onClick={handleFetchFromAPI}
                    disabled={loading.dailyRates}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 
                             disabled:opacity-50 disabled:cursor-not-allowed transition-colors 
                             flex items-center gap-2"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Fetch from Market
                  </button>
                  <button
                    onClick={handleCopyPreviousDay}
                    disabled={loading.dailyRates}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 
                             disabled:opacity-50 disabled:cursor-not-allowed transition-colors 
                             flex items-center gap-2"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
                      />
                    </svg>
                    Copy Previous Day
                  </button>
                </div>
              )}

              {/* Manual Input Form */}
              {(mode === "manual" || mode === "manual-from-api") && (
                <RateInputForm
                  onSubmit={handleManualSet}
                  onCancel={() => setMode("check")}
                  loading={loading.dailyRates}
                  initialData={getInitialFormData()}
                  mode={mode}
                />
              )}

              {/* API Rate Comparison */}
              {mode === "api" && apiRates && (
                <RateComparisonCard
                  currentRates={todayRate?.rates}
                  apiRates={apiRates.rates}
                  onConfirm={handleConfirmAPIRates}
                  onCancel={() => setMode("check")}
                  onEdit={handleEditAPIRates}
                />
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <svg
                className="w-12 h-12 mx-auto mb-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              Please select a branch to manage rates
            </div>
          )}

          {/* Error Display */}
          {errors.dailyRates && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800">{errors.dailyRates}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyRateManager;
